import { ExamCenterProctorRequestType } from '@exam-center/graphql';
import { ID_CARD_NUMBER_MASK, PHONE_NUMBER_MASK } from '@exam-center/utils';
import { englishTitleOptionSchema, thaiTitleOptionSchema } from '@shared/utils';
import dayjs from 'dayjs';
import IMask from 'imask';
import { z } from 'zod';

import { ACCEPTED_FILE_TYPES, MAX_FILE_LENGTH } from '../../constants';

const NAME_TH_PATTERN = /^[\u0E00-\u0E7F\s]+$/;
const NAME_EN_PATTERN = /^[A-Za-z\s]+$/;

const MIN_REQUIRED_FIELD_LENGTH = 1;
const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 256;
const MAX_ADDRESS_LENGTH = 1024;
const MAX_UPLOAD_SIZE = 1024 * 1024 * 20; // 20MB
const MAX_UPLOAD_SIZE_IN_MB = MAX_UPLOAD_SIZE / (1024 * 1024);

const attachedFileSchema = z
  .array(
    z
      .instanceof(File, {
        message: 'กรุณาแนบไฟล์อัปโหลด',
      })
      .refine((file) => {
        return ACCEPTED_FILE_TYPES.includes(file.type);
      }, 'รูปแบบไฟล์ไม่ถูกต้อง กรุณาอัปโหลดไฟล์ .jpg หรือ .jpeg หรือ .pdf เท่านั้น'),
  )
  .min(MIN_REQUIRED_FIELD_LENGTH, { message: 'กรุณาแนบไฟล์อัปโหลด' })
  .max(MAX_FILE_LENGTH, { message: `ไม่สามารถอัปโหลดได้เกิน ${MAX_FILE_LENGTH} ไฟล์` })
  .refine(
    (files) => {
      return files.length && files.reduce((acc, file) => acc + file.size, 0) <= MAX_UPLOAD_SIZE;
    },
    {
      message: `ขนาดไฟล์รวมกันต้องไม่เกิน ${MAX_UPLOAD_SIZE_IN_MB} MB`,
    },
  )
  .refine(
    (files) => {
      return files.every((file) => ACCEPTED_FILE_TYPES.includes(file.type));
    },
    {
      message: 'รูปแบบไฟล์ไม่ถูกต้อง กรุณาอัปโหลดไฟล์ .jpg หรือ .jpeg หรือ .pdf เท่านั้น',
    },
  );

export const ProctorRequestCreationFormSchema = z.object({
  proctorType: z.nativeEnum(ExamCenterProctorRequestType, {
    message: 'กรุณาเลือกประเภทผู้คุมสอบ',
  }),
  personal: z.object({
    idNumber: z
      .string()
      .trim()
      .min(MIN_REQUIRED_FIELD_LENGTH, {
        message: 'กรุณากรอกเลขบัตรประชาชน',
      })
      .refine(
        (val) => {
          const mask = IMask.createMask({ mask: ID_CARD_NUMBER_MASK });
          mask.resolve(val);
          return mask.isComplete;
        },
        { message: 'รูปแบบเลขบัตรประชาชนไม่ถูกต้อง ต้องมีจำนวน 13 หลักและเป็นตัวเลขเท่านั้น' },
      ),
    titleTh: thaiTitleOptionSchema,
    firstNameTh: z
      .string()
      .trim()
      .min(MIN_REQUIRED_FIELD_LENGTH, { message: 'กรุณากรอกชื่อภาษาไทย' })
      .max(MAX_NAME_LENGTH, `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`)
      .regex(NAME_TH_PATTERN, {
        message: 'กรุณากรอกชื่อเป็นภาษาไทย',
      }),
    lastNameTh: z
      .string()
      .trim()
      .min(MIN_REQUIRED_FIELD_LENGTH, { message: 'กรุณากรอกนามสกุลภาษาไทย' })
      .max(MAX_NAME_LENGTH, `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`)
      .regex(NAME_TH_PATTERN, {
        message: 'กรุณากรอกนามสกุลเป็นภาษาไทย',
      }),
    titleEn: englishTitleOptionSchema,
    firstNameEn: z
      .string()
      .trim()
      .min(MIN_REQUIRED_FIELD_LENGTH, { message: 'กรุณากรอกชื่อภาษาอังกฤษ' })
      .max(MAX_NAME_LENGTH, `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`)
      .regex(NAME_EN_PATTERN, {
        message: 'กรุณากรอกชื่อเป็นภาษาอังกฤษ',
      }),
    lastNameEn: z
      .string()
      .trim()
      .min(MIN_REQUIRED_FIELD_LENGTH, { message: 'กรุณากรอกนามสกุลภาษาอังกฤษ' })
      .max(MAX_NAME_LENGTH, `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`)
      .regex(NAME_EN_PATTERN, {
        message: 'กรุณากรอกนามสกุลเป็นภาษาอังกฤษ',
      }),
    email: z
      .string()
      .trim()
      .min(MIN_REQUIRED_FIELD_LENGTH, { message: 'กรุณากรอกอีเมลผู้ใช้' })
      .max(MAX_EMAIL_LENGTH, `ความยาวต้องไม่เกิน ${MAX_EMAIL_LENGTH} ตัวอักษร`)
      .email({
        message: 'รูปแบบอีเมลผู้ใช้ไม่ถูกต้อง',
      }),
    phoneNumber: z
      .string()
      .nullish()
      .refine(
        (val) => {
          if (!val) return true;
          const mask = IMask.createMask({ mask: PHONE_NUMBER_MASK });
          mask.resolve(val);
          return mask.isComplete;
        },
        { message: 'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง' },
      ),
    address: z
      .string()
      .trim()
      .max(MAX_ADDRESS_LENGTH, `ความยาวต้องไม่เกิน ${MAX_ADDRESS_LENGTH} ตัวอักษร`),
  }),
  employeeInfo: z
    .object({
      code: z.string().trim().nullish(),
      position: z.string().trim().nullish(),
      department: z.string().trim().nullish(),
      termination: z
        .object({
          isAssigned: z.enum(['true', 'false']),
          endedAt: z.date().nullish(),
        })
        .refine(({ isAssigned, endedAt }) => isAssigned === 'false' || endedAt !== null, {
          message: 'ระบุวันที่สิ้นสุดปฏิบัติหน้าที่',
          path: ['endedAt'],
        }),
      startedAt: z.date({ message: 'ระบุวันที่เริ่มปฏิบัติหน้าที่' }),
    })
    .refine(
      ({ termination, startedAt }) =>
        termination.isAssigned === 'false' ||
        dayjs(termination.endedAt).isSameOrAfter(dayjs(startedAt), 'date'),
      {
        message: 'วันที่สิ้นสุดปฏิบัติหน้าที่ต้องมากกว่าหรือเท่ากับวันที่เริ่มปฏิบัติหน้าที่',
        path: ['termination.endedAt'],
      },
    ),
  document: z.object({
    agreementForms: attachedFileSchema,
    identifications: attachedFileSchema,
  }),
});

export type ProctorRequestCreationFormInput = z.input<typeof ProctorRequestCreationFormSchema>;
